import { useState } from "react"
import { X, CreditCard, Shield, Clock, CheckCircle, ExternalLink, Loader2, Tag, XCircle } from "lucide-react"
import { Button } from "./ui/button"
import { usePost } from "../utils/apiComponent/useApi"
import { endpoints } from "../utils/endpoints"
import { showSuccessToast, showErrorToast } from "../utils/toast"
import { formatPrice } from "../utils/formatters"
import { useTranslation } from "../i18n"
import { usePromoCodes } from "../hooks/usePromoCodes"

export default function CoursePaymentModal({ 
  isOpen, 
  onClose, 
  course,
  onPaymentSuccess 
}) {
  const { tObj } = useTranslation()
  const [isProcessing, setIsProcessing] = useState(false)
  const [promoCode, setPromoCode] = useState("")
  const [validatedPromoCode, setValidatedPromoCode] = useState(null)
  const [isValidating, setIsValidating] = useState(false)
  const [discountAmount, setDiscountAmount] = useState(0)
  const [finalAmount, setFinalAmount] = useState(null)
  
  const { execute: post, loading } = usePost(endpoints.checkoutLink)
  const { validatePromoCode } = usePromoCodes()

  // Validation des props
  if (!isOpen) return null

  if (!course || !course.id) {
    console.error('CoursePaymentModal: course is missing or invalid')
    if (onClose && typeof onClose === 'function') {
      onClose()
    }
    return null
  }

  if (!onClose || typeof onClose !== 'function') {
    console.error('CoursePaymentModal: onClose is missing or invalid')
    return null
  }

  // Vérifier que le cours est payant
  const isFree = course.isFree || course.price === "0" || course.price === 0 || !course.price
  if (isFree) {
    console.warn('CoursePaymentModal: Course is free, closing modal')
    if (onClose) {
      onClose()
    }
    return null
  }

  const handleValidatePromoCode = async () => {
    if (!promoCode.trim()) {
      showErrorToast({ message: "Veuillez entrer un code promo" })
      return
    }

    if (!course?.id) {
      showErrorToast({ message: tObj.modals.coursePayment.courseInfoMissing() })
      return
    }

    setIsValidating(true)
    
    try {
      const cartAmount = parseFloat(course.price) || 0
      const response = await validatePromoCode(promoCode.trim().toUpperCase(), course.id, cartAmount)
      
      if (response?.isValid && response?.promoCode) {
        setValidatedPromoCode(response.promoCode)
        setDiscountAmount(response.discountAmount || 0)
        setFinalAmount(response.finalAmount || cartAmount)
        showSuccessToast({ message: response.messages?.[0] || "Code promo valide" })
      } else {
        setValidatedPromoCode(null)
        setDiscountAmount(0)
        setFinalAmount(null)
        showErrorToast({ message: "Code promo invalide" })
      }
    } catch (error) {
      setValidatedPromoCode(null)
      setDiscountAmount(0)
      setFinalAmount(null)
      showErrorToast({ message: error?.message || "Erreur lors de la validation du code promo" })
    } finally {
      setIsValidating(false)
    }
  }

  const handleRemovePromoCode = () => {
    setPromoCode("")
    setValidatedPromoCode(null)
    setDiscountAmount(0)
    setFinalAmount(null)
  }

  const handlePayment = async () => {
    // Vérifications supplémentaires avant le paiement
    if (!course?.id) {
      showErrorToast({ message: tObj.modals.coursePayment.courseInfoMissing() })
      return
    }

    if (!course.price || parseFloat(course.price) <= 0) {
      showErrorToast({ message: tObj.modals.coursePayment.invalidPrice() || 'Invalid course price' })
      return
    }

    setIsProcessing(true)
    
    try {
      console.log("Envoi de la requête de paiement pour le cours:", course.id)
      
      const requestBody = {
        courseId: course.id
      }
      
      // Ajouter le code promo si validé
      if (validatedPromoCode) {
        requestBody.promoCode = validatedPromoCode.code
      }
      
      const response = await post(requestBody)

      console.log("Réponse du serveur:", response)

      // L'apiClient extrait automatiquement data.data, donc response = { checkoutLink: "..." }
      if (response?.checkoutLink) {
        const checkoutLink = response.checkoutLink
        
        console.log("Lien de paiement reçu:", checkoutLink)
        
        // Rediriger vers le lien de paiement
        window.open(checkoutLink, '_blank')
        
        showSuccessToast({ message: tObj.modals.coursePayment.redirectingToPayment() })
        
        // Fermer le modal après un délai
        setTimeout(() => {
          if (onClose && typeof onClose === 'function') {
            onClose()
          }
          if (onPaymentSuccess && typeof onPaymentSuccess === 'function') {
            try {
              onPaymentSuccess(response)
            } catch (err) {
              console.error('Error in onPaymentSuccess callback:', err)
            }
          }
        }, 2000)
      } else {
        console.error("Réponse invalide du serveur:", response)
        showErrorToast({ 
          message: tObj.modals.coursePayment.paymentLinkError() 
        })
      }
    } catch (error) {
      console.error("Erreur de paiement complète:", error)
      showErrorToast({ 
        message: error?.message || tObj.modals.coursePayment.paymentError() 
      })
    } finally {
      setIsProcessing(false)
    }
  }

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center p-3 sm:p-4">
      {/* Backdrop */}
      <div 
        className="absolute inset-0 bg-black/50 backdrop-blur-sm"
        onClick={() => {
          if (!isProcessing && onClose && typeof onClose === 'function') {
            onClose()
          }
        }}
      />
      {/* Modal Content */}
      <div className="relative bg-white rounded-xl shadow-2xl max-w-md w-full max-h-[90vh] overflow-y-auto z-10">
        {/* Header */}
        <div className="flex items-center justify-between p-4 sm:p-5 md:p-6 border-b">
          <div className="flex items-center gap-2 sm:gap-3 flex-1 min-w-0">
            <div className="p-1.5 sm:p-2 bg-green-100 rounded-lg flex-shrink-0">
              <CreditCard className="w-4 h-4 sm:w-5 sm:h-5 md:w-6 md:h-6 text-green-600" />
            </div>
            <h2 className="text-lg sm:text-xl font-bold text-gray-900 truncate">
              {tObj.modals.coursePayment.title()}
            </h2>
          </div>
          <button
            onClick={() => {
              if (!isProcessing && onClose && typeof onClose === 'function') {
                onClose()
              }
            }}
            className="text-gray-400 hover:text-gray-600 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex-shrink-0 ml-2"
            disabled={isProcessing || loading}
            aria-label="Close modal"
          >
            <X className="w-4 h-4 sm:w-5 sm:h-5" />
          </button>
        </div>

        {/* Content */}
        <div className="p-4 sm:p-5 md:p-6 space-y-4 sm:space-y-6">
          {/* Course Info */}
          <div className="text-center">
            <h3 className="text-lg font-semibold text-gray-900 mb-2">
              {course?.title}
            </h3>
            <p className="text-gray-600 text-sm mb-4">
              {course?.shortDescription || course?.description}
            </p>
            
            {/* Price */}
            {course?.price && parseFloat(course.price) > 0 && (
              <div className="bg-gray-50 rounded-lg p-3 sm:p-4 mb-3 sm:mb-4">
                {finalAmount !== null && validatedPromoCode ? (
                  <>
                    <div className="text-2xl sm:text-3xl font-bold text-gray-900 mb-1">
                      {formatPrice(finalAmount, course.currency || 'XOF')}
                    </div>
                    <div className="text-base sm:text-lg text-gray-500 line-through">
                      {formatPrice(course.price, course.currency || 'XOF')}
                    </div>
                    <div className="text-xs sm:text-sm text-green-600 font-medium mt-1">
                      -{formatPrice(discountAmount, course.currency || 'XOF')} ({validatedPromoCode.discountPercentage}% de réduction)
                    </div>
                  </>
                ) : (
                  <>
                    <div className="text-2xl sm:text-3xl font-bold text-gray-900 mb-1">
                      {formatPrice(course.price, course.currency || 'XOF')}
                    </div>
                    {course.originalPrice && 
                     parseFloat(course.originalPrice) > 0 && 
                     parseFloat(course.originalPrice) !== parseFloat(course.price) && (
                      <div className="text-base sm:text-lg text-gray-500 line-through">
                        {formatPrice(course.originalPrice, course.currency || 'XOF')}
                      </div>
                    )}
                  </>
                )}
                <p className="text-xs sm:text-sm text-gray-600 mt-2">
                  {tObj.modals.coursePayment.lifetimeAccess()}
                </p>
              </div>
            )}

            {/* Promo Code Section */}
            <div className="space-y-2">
              {!validatedPromoCode ? (
                <div className="flex flex-col sm:flex-row gap-2">
                  <div className="flex-1 relative">
                    <Tag className="absolute left-2 sm:left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={16} />
                    <input
                      type="text"
                      placeholder="Code promo"
                      value={promoCode}
                      onChange={(e) => setPromoCode(e.target.value.toUpperCase())}
                      onKeyPress={(e) => {
                        if (e.key === 'Enter') {
                          handleValidatePromoCode()
                        }
                      }}
                      className="w-full pl-8 sm:pl-10 pr-3 sm:pr-4 py-2 text-sm sm:text-base border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#141835] focus:border-transparent"
                    />
                  </div>
                  <Button
                    onClick={handleValidatePromoCode}
                    disabled={isValidating || !promoCode.trim()}
                    variant="outline"
                    className="whitespace-nowrap text-sm sm:text-base"
                  >
                    {isValidating ? (
                      <Loader2 className="w-3 h-3 sm:w-4 sm:h-4 animate-spin" />
                    ) : (
                      "Appliquer"
                    )}
                  </Button>
                </div>
              ) : (
                <div className="bg-green-50 border border-green-200 rounded-lg p-2 sm:p-3 flex items-center justify-between gap-2">
                  <div className="flex items-center gap-2 flex-1 min-w-0">
                    <CheckCircle className="w-4 h-4 sm:w-5 sm:h-5 text-green-600 flex-shrink-0" />
                    <div className="flex-1 min-w-0">
                      <div className="text-xs sm:text-sm font-medium text-green-900 truncate">
                        Code promo: {validatedPromoCode.code}
                      </div>
                      {validatedPromoCode.description && (
                        <div className="text-xs text-green-700 truncate">
                          {validatedPromoCode.description}
                        </div>
                      )}
                    </div>
                  </div>
                  <button
                    onClick={handleRemovePromoCode}
                    className="text-green-600 hover:text-green-800 flex-shrink-0"
                    title="Retirer le code promo"
                  >
                    <XCircle size={16} />
                  </button>
                </div>
              )}
            </div>
          </div>

          {/* Features */}
          <div className="space-y-2 sm:space-y-3">
            <h4 className="font-medium text-sm sm:text-base text-gray-900">{tObj.modals.coursePayment.whatYouGet()}</h4>
            <div className="space-y-1.5 sm:space-y-2">
              <div className="flex items-center gap-2 sm:gap-3 text-xs sm:text-sm text-gray-600">
                <CheckCircle className="w-3 h-3 sm:w-4 sm:h-4 text-green-500 flex-shrink-0" />
                <span>{tObj.modals.coursePayment.fullAccess()}</span>
              </div>
              <div className="flex items-center gap-2 sm:gap-3 text-xs sm:text-sm text-gray-600">
                <CheckCircle className="w-3 h-3 sm:w-4 sm:h-4 text-green-500 flex-shrink-0" />
                <span>{tObj.modals.coursePayment.completionCertificate()}</span>
              </div>
              <div className="flex items-center gap-2 sm:gap-3 text-xs sm:text-sm text-gray-600">
                <CheckCircle className="w-3 h-3 sm:w-4 sm:h-4 text-green-500 flex-shrink-0" />
                <span>{tObj.modals.coursePayment.communitySupport()}</span>
              </div>
              <div className="flex items-center gap-2 sm:gap-3 text-xs sm:text-sm text-gray-600">
                <CheckCircle className="w-3 h-3 sm:w-4 sm:h-4 text-green-500 flex-shrink-0" />
                <span>{tObj.modals.coursePayment.freeUpdates()}</span>
              </div>
            </div>
          </div>

          {/* Security Info */}
          <div className="bg-blue-50 rounded-lg p-3 sm:p-4">
            <div className="flex items-start gap-2 sm:gap-3">
              <Shield className="w-4 h-4 sm:w-5 sm:h-5 text-blue-600 flex-shrink-0 mt-0.5" />
              <div className="flex-1 min-w-0">
                <h5 className="font-medium text-xs sm:text-sm text-blue-900 mb-1">{tObj.modals.coursePayment.securePayment()}</h5>
                <p className="text-xs sm:text-sm text-blue-700">
                  {tObj.modals.coursePayment.securePaymentDesc()}
                </p>
              </div>
            </div>
          </div>

          {/* Processing Info */}
          {isProcessing && (
            <div className="bg-yellow-50 rounded-lg p-3 sm:p-4">
              <div className="flex items-center gap-2 sm:gap-3">
                <Loader2 className="w-4 h-4 sm:w-5 sm:h-5 text-yellow-600 animate-spin flex-shrink-0" />
                <div className="flex-1 min-w-0">
                  <h5 className="font-medium text-xs sm:text-sm text-yellow-900 mb-1">{tObj.modals.coursePayment.generatingLink()}</h5>
                  <p className="text-xs sm:text-sm text-yellow-700">
                    {tObj.modals.coursePayment.generatingLinkDesc()}
                  </p>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="p-4 sm:p-5 md:p-6 border-t bg-gray-50 rounded-b-xl">
          <div className="space-y-2 sm:space-y-3">
            <Button
              onClick={handlePayment}
              disabled={isProcessing || loading}
              className="w-full bg-[#141835] hover:bg-[#1a1f4a] text-white h-10 sm:h-12 text-sm sm:text-base md:text-lg font-medium"
            >
              {isProcessing || loading ? (
                <>
                  <Loader2 className="w-4 h-4 sm:w-5 sm:h-5 mr-1 sm:mr-2 animate-spin" />
                  <span className="hidden sm:inline">{tObj.modals.coursePayment.processing()}</span>
                  <span className="sm:hidden">Traitement...</span>
                </>
              ) : (
                <>
                  <ExternalLink className="w-4 h-4 sm:w-5 sm:h-5 mr-1 sm:mr-2" />
                  <span className="truncate">{tObj.modals.coursePayment.proceedToPayment()}</span>
                </>
              )}
            </Button>
            
            <div className="flex items-center justify-center gap-1.5 sm:gap-2 text-xs text-gray-500">
              <Clock className="w-3 h-3 flex-shrink-0" />
              <span>{tObj.modals.coursePayment.instantPayment()}</span>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}
